import { defineStore } from "pinia";
import {
  CallControlsConfig,
  CustomerStatus,
  GetTenantsResponse,
  ProductCatalogConfig,
  Tenant,
  TenantConfigEntity,
} from "@/services/api/sdk";
import { computed, ref } from "vue";
import {
  CallControls,
  CallControlsProvider,
} from "@/services/call-controls/call-controls";
import { useLiveCallStore } from "@/store/contact/live-call";
import { useLocalStorage } from "@vueuse/core";
import { useApi } from "@/services/api";

const SELECTED_TENANT_KEY = "selectedTenant";

export const useClientConfig = defineStore("client-config", () => {
  const callControlsConfig = ref<CallControlsConfig>({});
  const productCatalogConfig = ref<ProductCatalogConfig>({});
  const callStatusesConfig = ref<CustomerStatus[]>([]);
  const tenantGlobalConfig = ref<TenantConfigEntity>({});
  const tenants = ref<Record<number, Tenant>>({});
  const selectedTenant = useLocalStorage(SELECTED_TENANT_KEY, -1);
  const callControls = ref<CallControls | null>(null);
  const callControlsOverride = ref<CallControls | null>(null);

  let tenantsPromise: Promise<void> | null = null;

  const callControlsOrOverride = computed(
    () => callControlsOverride.value || callControls.value,
  );

  function overrideCallControls(controls: CallControls) {
    callControlsOverride.value = controls;
  }

  function restoreCallControls() {
    callControlsOverride.value = null;
  }

  async function fetchConfig(): Promise<void> {
    const liveCall = useLiveCallStore();
    const api = useApi();

    const {
      productCatalogConfig: pcConfig,
      callControlsConfig: ccConfig,
      globalConfig,
      customerStatusConfig,
    } = await api.getConfig();

    tenantGlobalConfig.value = globalConfig || {};
    callStatusesConfig.value = customerStatusConfig || [];
    callControlsConfig.value = ccConfig || {};
    productCatalogConfig.value = pcConfig || {};

    var provider = await getCallControlsProvider();
    if (!provider) {
      return;
    }
    callControls.value = provider({
      getConfig() {
        return callControlsConfig.value.options as Record<string, string>;
      },
      fetchIncomingContact: liveCall.fetchContactBySourceId,
      resetCallStream: liveCall.resetCallStream,
      updateCallStream: liveCall.updateCallStream,
    });
  }

  async function getCallControlsProvider(): Promise<CallControlsProvider | null> {
    switch (callControlsConfig.value.type) {
      case "AmazonConnect":
        return (
          await import("@/services/call-controls/amazon-connect")
        ).createAmazonCallControlsProvider();
      default:
        return null;
    }
  }

  async function ensureTenants(): Promise<void> {
    if (tenantsPromise) {
      await tenantsPromise;
      return;
    }
    await fetchTenants();
  }

  async function fetchTenants(): Promise<void> {
    const api = useApi();
    tenantsPromise = (async () => {
      const tenantsResponse = await api.getTenants();
      tenants.value =
        tenantsResponse.data?.reduce(
          (acc, tenant) => {
            acc[tenant.id!] = tenant;
            return acc;
          },
          {} as Record<number, Tenant>,
        ) || {};
      if (selectedTenant.value === -1 && Object.keys(tenants.value).length == 1) {
        selectedTenant.value = parseInt(Object.keys(tenants.value)[0]);
      } else if (!tenants.value[selectedTenant.value]) {
        selectedTenant.value = -1;
      }
    })();

    await tenantsPromise;
  }

  const selectedTenantName = computed(
    () => tenants.value[selectedTenant.value]?.name || "",
  );

  const sortedCustomerStatuses = computed<CustomerStatus[]>(() => {
    return callStatusesConfig.value.sort((a, b) => a.order! - b.order!);
  });

  const sortedCustomerCategories = computed<CustomerStatus[]>(() => {
    return sortedCustomerStatuses.value.filter((x) => !x.categoryId);
  });

  const customerStatusesByCategory = computed<Record<number, CustomerStatus[]>>(
    () => {
      return sortedCustomerStatuses.value
        .filter((x) => !!x.categoryId)
        .reduce(
          (acc, x) => {
            if (!acc[x.categoryId!]) {
              acc[x.categoryId!] = [];
            }
            acc[x.categoryId!].push(x);
            return acc;
          },
          {} as Record<number, CustomerStatus[]>,
        );
    },
  );

  return {
    callControlsConfig,
    productCatalogConfig,
    tenantGlobalConfig,
    sortedCustomerCategories,
    customerStatusesByCategory,
    sortedCustomerStatuses,
    fetchConfig,
    callControls: callControlsOrOverride,
    overrideCallControls,
    restoreCallControls,
    ensureTenants,
    selectedTenant,
    tenants,
    selectedTenantName,
  };
});
